import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  alpha,
  createTheme,
  useTheme as useMuiTheme,
  Divider,
  Breadcrumbs,
  Link,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  Settings as SettingsIcon,
  Check as CheckIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  FormatListBulleted as ListIcon,
  Tune as TuneIcon,
  FormatColorFill as ColorFillIcon,
  TextFields as TextFieldsIcon,
  BorderAll as BorderAllIcon,
  DeviceHub as DeviceHubIcon
} from '@mui/icons-material';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../context/ThemeContext';
import useSidebar from '../hooks/useSidebar';

const Aparencia = () => {
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const { isDarkMode, toggleTheme } = useTheme();
  const baseTheme = useMuiTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  // Criando um tema personalizado
  const customTheme = createTheme({
    ...baseTheme,
    typography: {
      fontFamily: [
        'Montserrat',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
    },
  });

  // Telas disponíveis para customização
  const telasSistema = [
    {
      id: 'dashboard',
      nome: 'Dashboard',
      icon: <DashboardIcon />,
      descricao: 'Tela principal com resumo de atividades'
    },
    {
      id: 'emitir-senha',
      nome: 'Emissão de Senhas',
      icon: <ListIcon />,
      descricao: 'Tela de emissão de senhas para atendimento'
    },
    {
      id: 'usuarios',
      nome: 'Gestão de Usuários',
      icon: <PersonIcon />,
      descricao: 'Tela de gerenciamento de usuários do sistema'
    },
    {
      id: 'configuracoes',
      nome: 'Configurações Gerais',
      icon: <TuneIcon />,
      descricao: 'Central de configurações do sistema'
    },
    {
      id: 'painel-chamada',
      nome: 'Painel de Chamada',
      icon: <DeviceHubIcon />,
      descricao: 'Painel de chamada de senhas'
    }
  ];

  // Opções de customização
  const opcoesTema = [
    {
      id: 'modo-escuro',
      nome: 'Modo Escuro',
      icon: <PaletteIcon />,
      descricao: 'Alternar entre modo claro e escuro',
      ativo: isDarkMode
    },
    {
      id: 'cores-primarias',
      nome: 'Cores Primárias',
      icon: <ColorFillIcon />,
      descricao: 'Personalizar cores principais do sistema',
      ativo: false
    },
    {
      id: 'tipografia',
      nome: 'Tipografia',
      icon: <TextFieldsIcon />,
      descricao: 'Ajustar fontes e tamanhos de texto',
      ativo: false
    },
    {
      id: 'bordas',
      nome: 'Bordas e Sombras',
      icon: <BorderAllIcon />,
      descricao: 'Ajustar bordas e sombras dos elementos',
      ativo: false
    }
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Função para alternar a sidebar
  const handleSidebarToggle = () => {
    toggleSidebar();
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: alpha(customTheme.palette.primary.main, 0.03) }}>
      <Sidebar
        sidebarOpen={sidebarOpen}
        customTheme={customTheme}
      />

      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Header
          sidebarOpen={sidebarOpen}
          handleSidebarToggle={handleSidebarToggle}
          customTheme={customTheme}
        />

        <Container maxWidth="xxl" sx={{ mt: 10, mb: 4 }}>
          {/* Breadcrumbs para navegação */}
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            aria-label="breadcrumb"
            sx={{ mb: 3 }}
          >
            <Link 
              color="inherit" 
              onClick={(e) => {
                e.preventDefault();
                navigate('/dashboard');
              }}
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                textDecoration: 'none',
                color: 'text.primary',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Dashboard
            </Link>
            <Link 
              color="inherit" 
              onClick={(e) => {
                e.preventDefault();
                navigate('/configuracoes');
              }}
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                textDecoration: 'none',
                color: 'text.primary',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline',
                }
              }}
            >
              <SettingsIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Configurações
            </Link>
            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
              <PaletteIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Aparência
            </Typography>
          </Breadcrumbs>
          
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 4,
              border: `1px solid ${alpha(customTheme.palette.primary.main, 0.2)}`,
              background: theme => theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(customTheme.palette.grey[900], 0.95)} 0%, ${alpha(customTheme.palette.grey[800], 0.9)} 100%)`
                : 'background.paper',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              textAlign: 'center',
              mb: 4 
            }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  background: theme => theme.palette.mode === 'dark'
                    ? `linear-gradient(90deg, ${customTheme.palette.primary.light} 0%, ${customTheme.palette.primary.main} 100%)`
                    : `linear-gradient(90deg, ${customTheme.palette.primary.main} 0%, ${customTheme.palette.primary.dark} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Personalização de Aparência
              </Typography>
              
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ 
                  maxWidth: '700px',
                  mb: 3
                }}
              >
                Personalize a aparência visual do sistema para melhor atender às necessidades da sua organização. 
                As alterações visuais afetam todas as instâncias do sistema para todos os usuários.
              </Typography>
            </Box>

            {/* Tabs para alternar entre opções */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={activeTab} 
                onChange={handleTabChange} 
                aria-label="configuração de aparência"
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '1rem',
                  }
                }}
              >
                <Tab 
                  icon={<PaletteIcon />} 
                  iconPosition="start" 
                  label="Temas e Cores" 
                />
                <Tab 
                  icon={<DeviceHubIcon />} 
                  iconPosition="start" 
                  label="Telas do Sistema" 
                />
              </Tabs>
            </Box>

            {/* Conteúdo baseado na tab selecionada */}
            {activeTab === 0 && (
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2,
                    fontWeight: 600,
                    color: customTheme.palette.primary.main
                  }}
                >
                  Opções de Tema
                </Typography>
                
                <Grid container spacing={3}>
                  {opcoesTema.map((opcao) => (
                    <Grid item xs={12} sm={6} md={3} key={opcao.id}>
                      <Card 
                        sx={{ 
                          height: '100%',
                          borderRadius: 3,
                          position: 'relative',
                          transition: 'all 0.3s ease',
                          border: `1px solid ${alpha(customTheme.palette.primary.main, 0.2)}`,
                          '&:hover': {
                            boxShadow: `0 8px 24px ${alpha(customTheme.palette.primary.main, 0.25)}`,
                            transform: 'translateY(-4px)',
                            border: `1px solid ${alpha(customTheme.palette.primary.main, 0.4)}`,
                          }
                        }}
                      >
                        {opcao.id === 'modo-escuro' ? (
                          <CardActionArea 
                            onClick={toggleTheme}
                            sx={{ height: '100%' }}
                          >
                            <CardContent>
                              <Box 
                                sx={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  mb: 2
                                }}
                              >
                                <Box 
                                  sx={{ 
                                    display: 'flex',
                                    p: 1.5,
                                    borderRadius: '50%',
                                    bgcolor: alpha(customTheme.palette.primary.main, 0.1),
                                    color: customTheme.palette.primary.main,
                                    mr: 2
                                  }}
                                >
                                  {opcao.icon}
                                </Box>
                                <Typography variant="h6">{opcao.nome}</Typography>
                                
                                {/* Indicador de status */}
                                {opcao.ativo && (
                                  <Tooltip title="Modo Escuro Ativo">
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 12,
                                        bgcolor: customTheme.palette.primary.main,
                                        borderRadius: '50%',
                                        p: 0.5,
                                        color: 'white',
                                        display: 'flex'
                                      }}
                                    >
                                      <CheckIcon fontSize="small" />
                                    </Box>
                                  </Tooltip>
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {opcao.descricao}
                              </Typography>
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{ mt: 2 }}
                              >
                                {isDarkMode ? 'Mudar para Claro' : 'Mudar para Escuro'}
                              </Button>
                            </CardContent>
                          </CardActionArea>
                        ) : (
                          <CardContent>
                            <Box 
                              sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2
                              }}
                            >
                              <Box 
                                sx={{ 
                                  display: 'flex',
                                  p: 1.5,
                                  borderRadius: '50%',
                                  bgcolor: alpha(customTheme.palette.primary.main, 0.1),
                                  color: customTheme.palette.primary.main,
                                  mr: 2
                                }}
                              >
                                {opcao.icon}
                              </Box>
                              <Typography variant="h6">{opcao.nome}</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {opcao.descricao}
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              sx={{ mt: 2 }}
                              disabled
                            >
                              Em breve
                            </Button>
                          </CardContent>
                        )}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 2,
                    fontWeight: 600,
                    color: customTheme.palette.primary.main
                  }}
                >
                  Personalização por Tela
                </Typography>
                
                <Typography variant="body2" color="text.secondary" paragraph>
                  Selecione uma tela para personalizar seus elementos visuais específicos.
                </Typography>
                
                <Grid container spacing={3}>
                  {telasSistema.map((tela) => (
                    <Grid item xs={12} sm={6} md={4} key={tela.id}>
                      <Card 
                        sx={{ 
                          height: '100%',
                          borderRadius: 3,
                          transition: 'all 0.3s ease',
                          border: `1px solid ${alpha(customTheme.palette.primary.main, 0.2)}`,
                          '&:hover': {
                            boxShadow: `0 8px 24px ${alpha(customTheme.palette.primary.main, 0.25)}`,
                            transform: 'translateY(-4px)',
                            border: `1px solid ${alpha(customTheme.palette.primary.main, 0.4)}`,
                          }
                        }}
                      >
                        <CardActionArea sx={{ height: '100%', p: 1 }} disabled>
                          <CardContent>
                            <Box 
                              sx={{ 
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2
                              }}
                            >
                              <Box 
                                sx={{ 
                                  display: 'flex',
                                  p: 1.5,
                                  borderRadius: '50%',
                                  bgcolor: alpha(customTheme.palette.primary.main, 0.1),
                                  color: customTheme.palette.primary.main,
                                  mr: 2
                                }}
                              >
                                {tela.icon}
                              </Box>
                              <Typography variant="h6">{tela.nome}</Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {tela.descricao}
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              sx={{ mt: 2 }}
                              disabled
                            >
                              Em breve
                            </Button>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default Aparencia; 